import { Rating } from 'components/rating';
import React, { FunctionComponent, useCallback } from 'react';
import { Dimmer, Icon, Label, Loader, Pagination } from 'semantic-ui-react';
import styled from 'styled-components';
import { formatDateTime } from 'utils';
import { useReviewFilters } from '../hooks/useReviewFilters';
import { Review } from '../types/review.type';
import {
  getReviewReplyStatus,
  reviewReplyStatusLabels,
} from '../utils/getReviewReplyStatus';
import { ReviewCheckerLabel } from './ReviewCheckerLabel';
import { ReviewLockerLabel } from './ReviewLockerLabel';
import { ReviewSourceLabel } from './ReviewSourceLabel';
import { ReviewStatusLabel } from './ReviewStatusLabel';
import { ZoneButton } from './ZoneButton';

const PanelHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  width: 320px;
  padding: 0 2px;
`;

const SortLink = styled.a`
  cursor: pointer;
  color: rgba(0, 0, 0, 0.6);

  &:hover {
    color: rgba(0, 0, 0, 0.87);
  }
`;

const CardsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  background-color: #fff;
`;

const ReviewCard = styled.div<{ $active: boolean }>`
  border-bottom: 1px solid #e8e8e8;
  border-left: 3px solid
    ${(props): string => (props.$active ? '#2185d0' : 'transparent')};
  background-color: ${(props): string => (props.$active ? '#f0f7fc' : '#fff')};
  padding: 8px 10px;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: ${(props): string =>
      props.$active ? '#f0f7fc' : '#f5f8fa'};
  }
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(0, 0, 0, 0.5);
  font-size: 12px;
  margin-bottom: 8px;
`;

const CardName = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CardProRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 30px;
  margin-bottom: 8px;
`;

const CardProInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardProName = styled.div`
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardProTradeNames = styled.div`
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  font-style: italic;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PaginationContainer = styled.div`
  margin-top: 8px;

  .ui.pagination.menu {
    display: flex;
  }

  .ui.pagination.menu .item {
    flex: 1 1 auto;
    justify-content: center;
  }
`;

const getReplyLabel = (review: Review): React.ReactElement | null => {
  const replyStatus = getReviewReplyStatus(review);

  if (replyStatus === 'none') {
    return null;
  }

  const { text, color } = reviewReplyStatusLabels[replyStatus];

  return (
    <Label color={color} size="small" basic>
      Réponse {text.toLowerCase()}
    </Label>
  );
};

type ReviewListHeaderProps = {
  count: number;
};

export const ReviewListHeader: FunctionComponent<ReviewListHeaderProps> = ({
  count,
}) => {
  const { filters, setFilters } = useReviewFilters({});

  const orderByDirection = filters.orderBy?.direction ?? 'descending';

  return (
    <PanelHeader>
      <span>
        <b>{count}</b> avis
      </span>
      <SortLink
        onClick={(): void => {
          setFilters({
            orderBy: {
              column: 'dateCreated',
              direction:
                orderByDirection === 'ascending' ? 'descending' : 'ascending',
            },
            page: 1,
          });
        }}
      >
        <Icon
          name={
            orderByDirection === 'ascending'
              ? 'sort amount up'
              : 'sort amount down'
          }
        />
        Date
      </SortLink>
    </PanelHeader>
  );
};

type ReviewListPanelProps = {
  reviews?: Review[];
  loading: boolean;
  count: number;
  selectedReviewId?: number;
};

export const ReviewListPanel: FunctionComponent<ReviewListPanelProps> = ({
  reviews,
  loading,
  count,
  selectedReviewId,
}) => {
  const { filters, setFilters, reviewsPerPage } = useReviewFilters({});

  const page = filters.page ?? 1;
  const totalPages = Math.ceil(count / reviewsPerPage);

  const activeCardRef = useCallback((node: HTMLDivElement | null): void => {
    node?.scrollIntoView({ block: 'nearest' });
  }, []);

  return (
    <>
      <CardsContainer>
        <Dimmer.Dimmable>
          <Dimmer inverted verticalAlign="top" active={loading}>
            <Loader style={{ marginTop: '20px' }} inline>
              Chargement...
            </Loader>
          </Dimmer>
          {(reviews ?? []).map((review) => {
            const zoneProView = review.referenceView?.view;
            const presentation = zoneProView?.proPresentation;

            return (
              <ReviewCard
                key={review.id}
                ref={review.id === selectedReviewId ? activeCardRef : undefined}
                $active={review.id === selectedReviewId}
                onClick={(): void => {
                  setFilters({ selectedId: review.id });
                }}
              >
                <CardName>
                  <span>
                    {review.givenName} {review.familyName}
                  </span>
                  {review.rating ? (
                    <span style={{ flexShrink: 0 }}>
                      <Rating
                        value={review.rating}
                        color={review.ratingType === 'ia' ? 'green' : '#fbbd08'}
                      />
                    </span>
                  ) : null}
                </CardName>
                <CardTopRow>
                  <span>
                    {review.dateCreated
                      ? formatDateTime(review.dateCreated)
                      : null}
                  </span>
                  <span>
                    <ReviewSourceLabel source={review.source} size="mini" />
                  </span>
                </CardTopRow>
                <CardProRow>
                  <CardProInfo>
                    <CardProName>{presentation?.name}</CardProName>
                    {presentation?.mainTradeNames ? (
                      <CardProTradeNames>
                        {presentation.mainTradeNames}
                      </CardProTradeNames>
                    ) : null}
                  </CardProInfo>
                  {zoneProView ? <ZoneButton proView={zoneProView} /> : null}
                </CardProRow>
                <div>
                  <ReviewStatusLabel review={review} />
                  {getReplyLabel(review)}
                  <ReviewCheckerLabel review={review} size="mini" />
                  <ReviewLockerLabel review={review} size="mini" />
                </div>
              </ReviewCard>
            );
          })}
        </Dimmer.Dimmable>
      </CardsContainer>
      {totalPages > 1 ? (
        <PaginationContainer>
          <Pagination
            activePage={page}
            size="mini"
            siblingRange={1}
            boundaryRange={0}
            ellipsisItem={null}
            firstItem={{
              content: <Icon name="angle double left" />,
              icon: true,
            }}
            lastItem={{
              content: <Icon name="angle double right" />,
              icon: true,
            }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
            totalPages={totalPages}
            onPageChange={(_, data): void => {
              setFilters({ page: Number(data.activePage) });
            }}
          />
        </PaginationContainer>
      ) : null}
    </>
  );
};
